/**
 * Rótulos das fontes que a IA cita ao responder.
 *
 * O backend manda o nome da TABELA (`{ tabela: 'pm_task', registros: 12 }`) —
 * é o identificador real de onde o dado saiu, e é o que serve para depurar.
 * Para o gestor, `crm_atividades_sugeridas_automatizadas` não diz nada.
 *
 * O mapa foi levantado varrendo os emissores do backend
 * (`grep -rn "'tabela':\|\"tabela\":" src/aawz_intelligence_v2` — o módulo
 * INTEIRO, não só `tools`/`core`: `core/visoes/leituras.py` e `familia.py`
 * emitem `Fonte(tabela=...)` que o Cliente 360 repassa), não só as tabelas
 * que apareceram numa tela. São 61 hoje, contando as que chegam por variável
 * (`_tarefas_montagem.py` resolve para `pm_sprint`/`pm_folder`;
 * `core/partners_data.py` resolve para as quatro `app_*`).
 *
 * Histórico das reconferências — cada uma achou tabela emitida sem rótulo:
 * - 31/08/2026: faltavam `crm_relacionamento_regua_cliente`, os
 *   `s3_cotizacao_*` da carteira, `crm_oportunidade_funil*`,
 *   `financial_planning_simulacoes_evolucao_patrimonial`,
 *   `atendimento_conversa`, `bi_v2_dashboard`; e, no mesmo dia,
 *   `crm_lead`/`crm_funil_vendas`/`crm_funil_vendas_etapa`, da tool nova
 *   `tool_get_funil_leads.py` — o funil de LEADS, anterior e separado do de
 *   oportunidades.
 * - 14/09/2026: o print de um cliente mostrou `aawz_intelligence_v2_kb_chunks`
 *   cru no chip. Faltavam 25: as `aawz_intelligence_v2_*` de conhecimento,
 *   memória, sessão e skills; transcrições; família; agenda; contratos;
 *   honorários CVM; fee fixo; diagnóstico de carteira. Desde então
 *   `Fontes.unit.js` varre o backend irmão e fica vermelho para tabela
 *   emitida sem rótulo.
 *
 * ⚠️ `crm_lead` (a entidade) e `lead_kyc` (enriquecimento BigDataCorp salvo)
 * são tabelas DIFERENTES que nada têm a ver uma com a outra — só o nome
 * confunde. `lead_kyc` não fala de "leads" no sentido do funil; por isso o
 * rótulo dela é "Dados de KYC", não "Leads".
 *
 * ⚠️ Este mapa é um ESPELHO MANUAL do backend e envelhece sozinho: tool nova
 * que consulte tabela nova não aparece aqui. Duas redes de segurança: a
 * junção em `Fontes.unit.js` (acende no PR) e o fallback de `rotuloDaTabela`
 * (humaniza o que ainda assim escapar — nunca o nome cru na tela do cliente).
 *
 * Rótulo tem teto de 32 caracteres (cabe na linha do popover sem quebrar) e
 * fala a língua das telas: nada de sigla interna nem `snake_case`.
 */
export const ROTULO_POR_TABELA = {
  // Tarefas / projetos
  pm_task: 'Tarefas',
  pm_task_history: 'Histórico de tarefas',
  pm_sprint: 'Listas de tarefas',
  pm_folder: 'Pastas de tarefas',
  pm_comment: 'Comentários de tarefas',
  aawz_intelligence_changelogs: 'Alterações feitas pela IA',

  // Clientes e carteira
  app_clientes: 'Clientes',
  // spec-005-segmentacao-carteira — a régua de custódia por faixa, que
  // `segmentar_carteira` cita como fonte da classificação.
  // 20 caracteres: o teto do popover é 32, e "do escritório" no fim estourava.
  escritorio_segmentacao_profissionais: 'Régua de segmentação',
  app_dados_ativos_clientes: 'Ativos dos clientes',
  app_aplicacao_ativo: 'Movimentações de ativos',
  app_conta_bancaria: 'Contas bancárias',
  book_produtos_json: 'Book de produtos',
  app_grupo_familiar: 'Grupos familiares',
  app_clientes_grupo_familiar: 'Vínculos familiares',
  app_profissional_clientes: 'Carteira por profissional',
  partners_calendario: 'Agenda de reuniões',
  partners_contratos_documentos: 'Contratos e documentos',

  // CRM e comercial
  partners_oportunidades: 'Oportunidades',
  crm_atividades_sugeridas_automatizadas: 'Atividades sugeridas',
  crm_mensagem_canal: 'Mensagens do CRM',
  crm_relacionamento_regua_cliente: 'Régua de relacionamento',
  // 15/09/2026: a tool `get_regua_relacionamento_do_cliente` (régua de UM
  // cliente) também cita a régua cadastrada e as atividades dela.
  crm_relacionamento_regua: 'Réguas de relacionamento',
  crm_relacionamento_regua_atividade: 'Atividades da régua',
  crm_oportunidade_funil: 'Funil de vendas',
  crm_oportunidade_funil_etapa: 'Etapas do funil de vendas',
  crm_lead: 'Leads',
  crm_funil_vendas: 'Funil de leads',
  crm_funil_vendas_etapa: 'Etapas do funil de leads',
  lead_kyc: 'Dados de KYC',
  crm_historico_atividade_cliente: 'Histórico de atividades',

  // Atendimento e solicitações
  atendimento_mensagem: 'Mensagens de atendimento',
  atendimento_conversa: 'Conversas de atendimento',
  aawz_servicos_solicitacoes: 'Solicitações',
  aawz_servicos_solicitacoes_tipo: 'Tipos de solicitação',
  aawz_transcripts: 'Transcrições de reuniões',
  // spec-002-pos-reuniao-follow-up — as duas fontes que `get_nota_de_reuniao`
  // cita no rodapé de procedência da nota.
  aawz_transcript_summaries: 'Resumos de reuniões',
  aawz_intelligence_suggestions: 'Fila de sugestões da IA',
  aawz_transcripts_segments: 'Trechos de reuniões',

  // Planejamento financeiro
  financial_planning_simulacoes: 'Simulações de planejamento',
  // "Projeção", não "Evolução": a tool é `tool_get_projecao_patrimonial.py`
  // e o nome distingue da `s3_cotizacao_pl_historico` (o realizado).
  financial_planning_simulacoes_evolucao_patrimonial: 'Projeção patrimonial',
  financial_planning_objetivos: 'Objetivos de planejamento',
  financial_planning_transacoes: 'Transações de planejamento',
  financial_planning_receitas_e_despesas_esperadas:
    'Receitas e despesas previstas',

  // Cotização e carteira
  s3_cotizacao_pl_por_ativo: 'Posição por ativo',
  s3_cotizacao_pl_historico: 'Evolução patrimonial',
  s3_cotizacao_retornos_por_ativo: 'Retornos por ativo',
  s3_cotizacao_retornos_diarios: 'Rentabilidade diária',
  s3_cotizacao_returns: 'Retornos da carteira',
  landing_page_diagnostico: 'Diagnóstico de carteira',

  // Fee fixo (consultoria)
  fee_fixo_carteira_upload: 'Extratos importados',
  metodo_comercial_fee_fixo: 'Método comercial',
  partners_fee_fixo_relatorio_envios: 'Relatórios enviados',
  partners_fee_fixo_roleplay_sessao: 'Sessões de ensaio',
  partners_fee_fixo_roleplay_avaliacao: 'Avaliações de ensaio',

  // Honorários (consultor CVM)
  cvm_cobranca: 'Cobranças de honorários',
  cvm_recebimento: 'Recebimentos de honorários',

  // Comissionamento
  // 17/09/2026: a persona Especialista em Comissionamento estreou com
  // `tool_comissoes.py`. `explicacao_do_comissionamento` não é tabela — é o
  // texto versionado de `core/ia/explicacao_texto.py`, declarado em `sources`
  // porque o contrato do front é lista de dicts (lista vazia se leria como
  // "não consultei nada").
  // ⚠️ [merge 21/09/2026] As duas chaves que moravam aqui
  // (`comissionamento_pagamento`, `explicacao_do_comissionamento`) estavam
  // DUPLICADAS no bloco "Comissionamento" mais abaixo (`no-dupe-keys`; em JS a
  // última vence). Ficaram lá, com as irmãs — `_fechamento_mensal` é o
  // "Fechamento", `_pagamento` é o pagamento.

  // BI
  bi_v2_dashboard: 'Dashboards de BI',

  // Compliance e escritório
  compliance_clientes: 'Compliance de clientes',
  compliance_formulario_resposta: 'Respostas de formulário',
  aawz_escritorio_maturidade: 'Maturidade do escritório',

  // Conhecimento e memória da IA
  aawz_intelligence_v2_knowledge_items: 'Conhecimento do escritório',
  aawz_intelligence_v2_kb_chunks: 'Documentos do escritório',
  aawz_intelligence_v2_memoria: 'Memória da IA',
  aawz_intelligence_v2_perfil_profissional: 'Perfil do profissional',
  aawz_intelligence_v2_mensagens: 'Conversas anteriores',
  aawz_intelligence_v2_skills: 'Habilidades da IA',
  // [merge 21/09/2026] `tool_ler_anexo_analise.py` (spec-009, migration v673):
  // o arquivo que o consultor anexou e a IA leu. Sem rotulo, a tabela crua
  // aparecia no chip — o mesmo defeito do `kb_chunks` de 14/09.
  aawz_intelligence_v2_anexos_analise: 'Arquivo anexado na conversa',

  // ── Office 360: o que o GESTOR vê do escritório ───────────────────────
  // (`core/visoes/leituras_office/*` e `facetas_office/*`.) Entraram no mapa
  // em 19/09/2026, quando a junção acusou 40 tabelas emitidas sem rótulo.
  'dados_profissional': 'Profissionais',
  // Duas tabelas, dois rótulos: o cadastro geral e o do escritório. Rótulo
  // repetido faria duas linhas idênticas no popover de Fontes.
  'dados_profissional_partners': 'Profissionais do escritório',
  escritorio_equipe: 'Equipes',
  escritorio_filiais: 'Filiais',
  escritorio_funcionalidades: 'Funcionalidades contratadas',
  partners_professional_churn: 'Saída de profissionais',
  partners_meta_auc_escritorio: 'Meta de AuC',
  partners_meta_receita: 'Meta de receita',
  partners_gestao_desempenho_formulario_resposta: 'Avaliações de desempenho',
  partners_gestao_desempenho_profissional_questao_meta: 'Metas de desempenho',
  partners_gestao_desempenho_questao_resposta: 'Respostas de desempenho',
  partners_historico_captacao_clientes: 'Captação por cliente',
  partners_historico_captacao_indicadores: 'Indicadores de captação',
  partners_crm_iea_indice_saude_cliente: 'Índice de saúde do cliente',
  partners_partnership_stock_options: 'Stock options',
  partners_historical_valuation: 'Valuation do escritório',
  app_workflow: 'Fluxos de trabalho',
  app_workflow_execution: 'Execuções de fluxo',
  // ⚠️ Chaves com SUFIXO são o mesmo dado com recorte declarado pelo emissor
  // (`:sem_id_profissional`) ou o nome da função entre parênteses. O
  // consultor não precisa da diferença — o rótulo é o do dado.
  'partners_oportunidades:sem_id_profissional': 'Oportunidades do escritório',
  'pm_task (usecase_overview_stats)': 'Tarefas',
  'aawz_intelligence_v2_knowledge_items (KnowledgeService.list_items)':
    'Conhecimento do escritório',
  // Emissores que citam a FUNÇÃO/módulo em vez da tabela. Rotulados pelo que
  // a leitura produz, que é o que o consultor reconhece.
  'auc_profissionais_escritorio.calcular_auc_escritorio': 'AuC do escritório',
  'core.contexto_consultor.montar_situacao': 'Situação do consultor',

  // ── DRE e financeiro do escritório ────────────────────────────────────
  partners_financial_orcamento_dre_computed: 'Orçamento do DRE',
  partners_projecao_dre_computada: 'Projeção do DRE',
  partners_projecao_plano_expnsao_meta_dre_computada: 'Metas do plano de expansão',
  partners_configuracao_dre_categoria: 'Categorias do DRE',
  partners_status_motor_dre: 'Situação do cálculo do DRE',
  partners_financial_dre_versions: 'Versões do DRE',
  // ⚠️ NÃO são tabelas: `leituras_office/dre.py` emite `Fonte(tabela=
  // API.__name__)` — o nome da CLASSE Python da API do DRE. São só estes
  // dois valores (`dre.py:116`: competência quando o escritório usa o
  // regime, senão caixa). Sem rótulo, o consultor leria
  // "APITempPartnersDreEscritorioCompetencia" no chip de Fontes.
  // Reportado como defeito do backend (a fonte deveria nomear a visão, não
  // a classe); enquanto não muda, o rótulo é a rede.
  APITempPartnersDreEscritorio: 'DRE do escritório',
  APITempPartnersDreEscritorioCompetencia: 'DRE por competência',

  // ── Comissionamento ───────────────────────────────────────────────────
  comissionamento_regra: 'Regras de comissão',
  comissionamento_comissoes: 'Comissões',
  comissionamento_pagamento: 'Pagamentos de comissão',
  comissionamento_ajuste: 'Ajustes de comissão',
  comissionamento_comissao_minima: 'Comissão mínima',
  comissionamento_fechamento_mensal: 'Fechamento de comissões',
  // As quatro abaixo chegam pelos emissores DINÂMICOS das tools de escrita
  // (`esquema.tabela`, `self.TABELA`, `pendente['entidade']`) — resolvem
  // para `__tablename__` destes models (`aawz_commissions/core/ia/escrita/`).
  comissionamento_troca_de_campos: 'Troca de campos da comissão',
  comissionamento_imposto: 'Impostos da comissão',
  comissionamento_variaveis: 'Variáveis da comissão',
  comissionamento_lista: 'Listas da comissão',
  // 17/09/2026: a persona Especialista em Comissionamento estreou com
  // `tool_comissoes.py`. `explicacao_do_comissionamento` não é tabela — é o
  // texto versionado de `core/ia/explicacao_texto.py`, declarado em `sources`
  // porque o contrato do front é lista de dicts (lista vazia se leria como
  // "não consultei nada").
  explicacao_do_comissionamento: 'Explicação da comissão',

  // ── Ponte entre as bases de cliente ───────────────────────────────────
  clientes_partners: 'Base de clientes',
  de_para_clientes_partners: 'Vínculo entre bases',

  // ── Preparação de reunião (spec-001 `preparacao-reuniao`) ─────────────
  // `numeros_reuniao.py` [T030-T034] e `planejamento_reuniao.py` [T036-T037].
  portfolio_consolidation: 'Carteira consolidada',
  financial_planning_orcamento: 'Orçamento do planejamento',
  // ⚠️ As duas abaixo NÃO são tabela: `noticias_reuniao.py` emite `noticia`
  // (o provedor externo) e `tool_preparar_reuniao.py` emite `preparo_reuniao`
  // (o próprio documento montado). Entram aqui pelo mesmo motivo dos nomes de
  // CLASSE já registrados: sem rótulo, o consultor lê o identificador cru no
  // chip de Fontes. O conserto de raiz é o backend nomear a fonte real.
  noticia: 'Notícias do mercado',
  preparo_reuniao: 'Preparação de reunião',

  // Interno — nunca deveria chegar a um escritório real
  echo_test: 'Teste interno',
}

// Prefixos que só dizem ONDE a tabela mora, não O QUE ela guarda. Do mais
// longo para o mais curto: `aawz_intelligence_v2_` antes de `aawz_`.
const PREFIXOS_TECNICOS = [
  'aawz_intelligence_v2_',
  'aawz_intelligence_',
  'aawz_',
  'app_',
  'crm_',
  'partners_',
  's3_cotizacao_',
  'bi_v2_',
  'temp_',
  'pm_',
]

// `tool_get_indicador_bi.py:330` é o único emissor DINÂMICO: manda
// `bi:<nome do painel>/<componente>`, onde `componente` é o título do
// componente OU (sem título) o uuid dele (`_bi_projecao.py`:
// `options.title or componente.id or type`). Um cliente viu
// `bi:Receita Estimada/ace484bf-…` cru no chip (14/09/2026). O `bi:` é uma
// FAMÍLIA: o painel é o que o consultor reconhece, o componente é chave
// interna — agrupa por painel, nunca mostra o uuid.
export const PREFIXO_BI = 'bi:'
const SEPARADOR_BI = '/'
const ROTULO_BI = 'BI · '
const ROTULO_BI_SEM_PAINEL = 'Dashboard de BI'

export const ehFonteDeBi = (tabela) => typeof tabela === 'string' && tabela.startsWith(PREFIXO_BI)

const painelDaFonteDeBi = (tabela) =>
  tabela.slice(PREFIXO_BI.length).split(SEPARADOR_BI)[0].replaceAll(/_/g, ' ').trim()

/**
 * Chave pela qual `agruparFontes` consolida as linhas. Tabela comum é a
 * própria; fonte de BI é `bi:<painel>` — dois componentes do mesmo painel
 * viram UMA linha, e o uuid não vira nem chave nem texto.
 *
 * @param {String} tabela Nome como o backend mandou.
 * @returns {String}
 */
export const chaveDaFonte = (tabela) => {
  if (!ehFonteDeBi(tabela)) return tabela
  const painel = painelDaFonteDeBi(tabela)
  return painel ? PREFIXO_BI + painel : PREFIXO_BI
}

const rotuloDeBi = (tabela) => {
  const painel = painelDaFonteDeBi(tabela)
  return painel ? ROTULO_BI + painel : ROTULO_BI_SEM_PAINEL
}

const capitalizar = (texto) => texto.charAt(0).toUpperCase() + texto.slice(1)

const semPrefixoTecnico = (tabela) => {
  const prefixo = PREFIXOS_TECNICOS.find((p) => tabela.startsWith(p))
  const resto = prefixo ? tabela.slice(prefixo.length) : tabela
  return resto || tabela
}

/**
 * Nome legível para uma tabela que NÃO está no mapa.
 *
 * `aawz_intelligence_v2_kb_chunks` → "Kb chunks"; `bi:Captação/x` →
 * "BI · Captação". Não é bonito como o rótulo curado — é o piso: nunca
 * `snake_case` nem uuid na tela de um cliente.
 *
 * @param {String} tabela Nome técnico.
 * @returns {String}
 */
export const humanizarTabela = (tabela) => {
  const nome = String(tabela || '').trim()
  if (!nome) return ''
  if (ehFonteDeBi(nome)) return rotuloDeBi(nome)
  const legivel = semPrefixoTecnico(nome).replaceAll(/_/g, ' ').replaceAll(/\s+/g, ' ').trim()
  return capitalizar(legivel)
}

/**
 * Rótulo de exibição de uma tabela.
 *
 * Sem entrada no mapa a fonte NÃO some — esconder o que não foi mapeado faria
 * a resposta parecer ter consultado menos coisa do que consultou. Mas também
 * não sai crua: um cliente já viu `aawz_intelligence_v2_kb_chunks` no chip
 * (14/09/2026). Cai em `humanizarTabela`.
 *
 * @param {String} tabela Nome da tabela como o backend mandou.
 * @returns {String}
 */
export const rotuloDaTabela = (tabela) =>
  ROTULO_POR_TABELA[tabela] || humanizarTabela(tabela) || tabela
